import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User as FirebaseUser, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase/config';
import { 
  authenticateWithTelegram, 
  signInWithEmail, 
  signUpWithEmail,
  signInWithGoogle,
  logoutUser, 
  getUserData, 
  UserData,
  linkTelegramAccount,
  authenticateWithTelegramWebApp,
} from '../firebase/auth';
import { getTelegramInitData, initTelegramMiniApp, isTelegramMiniApp } from '../telegram/webApp';

interface AuthContextType {
  user: UserData | null;
  firebaseUser: FirebaseUser | null;
  loginWithEmail: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, displayName?: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  loginWithTelegram: (telegramData: any) => Promise<void>;
  loginWithTelegramMiniApp: () => Promise<void>;
  linkTelegram: (initData: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  isAuthenticated: boolean;
  isMiniApp: boolean;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [user, setUser] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [isMiniApp] = useState<boolean>(() => isTelegramMiniApp());

  useEffect(() => {
    initTelegramMiniApp();

    // Dans la Mini App Telegram : une seule tentative de connexion automatique via initData
    let miniAppLoginAttempted = false;

    // Observer les changements d'authentification Firebase
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setFirebaseUser(user);
      
      if (user) {
        // Charger les données utilisateur depuis Firestore
        const userData = await getUserData(user.uid);
        setUser(userData);
      } else {
        setUser(null);

        if (isMiniApp && !miniAppLoginAttempted) {
          miniAppLoginAttempted = true;
          try {
            // onAuthStateChanged sera rappelé avec l'utilisateur connecté
            await authenticateWithTelegramWebApp(getTelegramInitData());
            return;
          } catch (error) {
            console.error('Telegram Mini App auto-login failed:', error);
          }
        }
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, [isMiniApp]);

  const loginWithEmail = async (email: string, password: string) => {
    try {
      setLoading(true);
      await signInWithEmail(email, password);
      // L'état sera mis à jour automatiquement par onAuthStateChanged
    } catch (error) {
      console.error('Login error:', error);
      setLoading(false);
      throw error;
    }
  };

  const signUp = async (email: string, password: string, displayName?: string) => {
    try {
      setLoading(true);
      await signUpWithEmail(email, password, displayName);
      // L'état sera mis à jour automatiquement par onAuthStateChanged
    } catch (error) {
      console.error('Sign up error:', error);
      setLoading(false);
      throw error;
    }
  };

  const loginWithGoogle = async () => {
    try {
      setLoading(true);
      await signInWithGoogle();
      // L'état sera mis à jour automatiquement par onAuthStateChanged
    } catch (error) {
      console.error('Google login error:', error);
      setLoading(false);
      throw error;
    }
  };

  const loginWithTelegram = async (telegramData: any) => {
    try {
      setLoading(true);
      // Authentifier via Telegram et obtenir le custom token Firebase
      await authenticateWithTelegram(telegramData);
      // L'état sera mis à jour automatiquement par onAuthStateChanged
    } catch (error) {
      console.error('Login error:', error);
      setLoading(false);
      throw error;
    }
  };

  const loginWithTelegramMiniApp = async () => {
    const initData = getTelegramInitData();
    if (!initData) {
      throw new Error('Telegram Mini App initData not available');
    }
    try {
      setLoading(true);
      await authenticateWithTelegramWebApp(initData);
      // L'état sera mis à jour automatiquement par onAuthStateChanged
    } catch (error) {
      console.error('Telegram Mini App login error:', error);
      setLoading(false);
      throw error;
    }
  };

  const linkTelegram = async (initData: string) => {
    try {
      setLoading(true);
      await linkTelegramAccount(initData);
      // Recharger les données utilisateur
      if (firebaseUser) {
        const userData = await getUserData(firebaseUser.uid);
        setUser(userData);
      }
    } catch (error) {
      console.error('Link Telegram error:', error);
      setLoading(false);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = async () => {
    if (!firebaseUser) return;
    const userData = await getUserData(firebaseUser.uid);
    setUser(userData);
  };

  const logout = async () => {
    try {
      setLoading(true);
      await logoutUser();
      setUser(null);
      setFirebaseUser(null);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        firebaseUser,
        loginWithEmail,
        signUp,
        loginWithGoogle,
        loginWithTelegram,
        loginWithTelegramMiniApp,
        linkTelegram,
        logout,
        refreshUser,
        isAuthenticated: !!firebaseUser && !!user,
        isMiniApp,
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
